import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject } from '@angular/core';

const GAP = 8;
const VIEWPORT_MARGIN = 8;

/**
 * Tooltip global. Cualquier elemento visible del documento puede mostrarlo con `data-tooltip="texto"`.
 * El nodo vive en `body`, o dentro del `dialog` abierto si el elemento está en un modal, para que
 * no quede tapado por la capa superior del diálogo ni por un ancestro con `overflow: hidden`.
 */
@Injectable({ providedIn: 'root' })
export class GlobalTooltip {
  private readonly document = inject(DOCUMENT);
  private readonly tip: HTMLDivElement;
  private current: Element | null = null;
  private describedBy: string | null = null;

  constructor() {
    const tip = this.document.createElement('div');
    tip.id = 'app-tooltip';
    tip.className = 'app-tooltip';
    tip.setAttribute('role', 'tooltip');
    tip.hidden = true;
    this.document.body.append(tip);
    this.tip = tip;

    const doc = this.document;
    const onEnter = (event: Event) => this.onEnter(event.target);
    const onLeave = (event: MouseEvent | FocusEvent) => this.onLeave(event.target, event.relatedTarget);
    const onReposition = () => {
      if (this.current) this.place(this.current);
    };

    doc.addEventListener('mouseover', onEnter);
    doc.addEventListener('mouseout', onLeave);
    doc.addEventListener('focusin', onEnter);
    doc.addEventListener('focusout', onLeave);
    doc.addEventListener('scroll', onReposition, true);
    doc.defaultView?.addEventListener('resize', onReposition);

    inject(DestroyRef).onDestroy(() => {
      doc.removeEventListener('mouseover', onEnter);
      doc.removeEventListener('mouseout', onLeave);
      doc.removeEventListener('focusin', onEnter);
      doc.removeEventListener('focusout', onLeave);
      doc.removeEventListener('scroll', onReposition, true);
      doc.defaultView?.removeEventListener('resize', onReposition);
      this.unlink();
      tip.remove();
    });
  }

  private onEnter(target: EventTarget | null): void {
    const el = hostOf(target);
    if (el) this.show(el);
  }

  private onLeave(target: EventTarget | null, related: EventTarget | null): void {
    const el = hostOf(target);
    if (!el || el !== this.current) return;
    if (related instanceof Node && el.contains(related)) return;
    const active = this.document.activeElement;
    if (el.matches(':hover') || active === el || (active instanceof Node && el.contains(active))) return;
    this.hide();
  }

  private show(el: Element): void {
    const text = el.getAttribute('data-tooltip')?.trim() ?? '';
    if (!text || !isVisible(el)) {
      if (this.current === el) this.hide();
      return;
    }
    if (this.current !== el) {
      this.unlink();
      this.current = el;
      this.link(el);
    }
    this.tip.textContent = text;
    this.tip.hidden = false;
    this.place(el);
  }

  private hide(): void {
    this.unlink();
    this.current = null;
    this.tip.hidden = true;
    this.tip.classList.remove('is-below');
  }

  private link(el: Element): void {
    this.describedBy = el.getAttribute('aria-describedby');
    const next = this.describedBy ? `${this.describedBy} ${this.tip.id}` : this.tip.id;
    el.setAttribute('aria-describedby', next);
  }

  private unlink(): void {
    if (!this.current) return;
    if (this.describedBy === null) this.current.removeAttribute('aria-describedby');
    else this.current.setAttribute('aria-describedby', this.describedBy);
    this.describedBy = null;
  }

  private place(el: Element): void {
    const layer = layerFor(el, this.document.body);
    if (this.tip.parentElement !== layer) layer.append(this.tip);

    const rect = el.getBoundingClientRect();
    const tipRect = this.tip.getBoundingClientRect();
    const view = this.document.defaultView;
    const viewportWidth = view?.innerWidth ?? 0;
    const viewportHeight = view?.innerHeight ?? 0;

    let left = rect.left + rect.width / 2 - tipRect.width / 2;
    if (viewportWidth > 0) {
      const maxLeft = Math.max(VIEWPORT_MARGIN, viewportWidth - tipRect.width - VIEWPORT_MARGIN);
      left = Math.min(Math.max(VIEWPORT_MARGIN, left), maxLeft);
    }

    const above = rect.top - tipRect.height - GAP;
    const below = viewportHeight === 0 || above >= VIEWPORT_MARGIN;
    this.tip.classList.toggle('is-below', !below);
    this.tip.style.left = `${left}px`;
    this.tip.style.top = `${below ? above : rect.bottom + GAP}px`;

    const rawArrow = rect.left + rect.width / 2 - left;
    const arrow = Math.min(Math.max(12, rawArrow), Math.max(12, tipRect.width - 12));
    this.tip.style.setProperty('--tooltip-arrow', `${arrow}px`);
  }
}

function hostOf(target: EventTarget | null): Element | null {
  if (!(target instanceof Element)) return null;
  return target.closest('[data-tooltip]');
}

function layerFor(el: Element, body: HTMLElement): HTMLElement {
  const dialog = el.closest('dialog');
  if (dialog instanceof HTMLDialogElement && dialog.open) return dialog;
  return body;
}

function isVisible(el: Element): boolean {
  const view = el.ownerDocument.defaultView;
  if (!view) return false;
  for (let node: Element | null = el; node; node = node.parentElement) {
    if (node instanceof HTMLElement && node.hidden) return false;
    const style = view.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return true;
}
